/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './SavedCardRadioOption.scss';

import * as React from 'react';
import cx from 'classnames';

import { Icon, Text } from 'sarsaparilla';

import {
    imageSrcMap,
    getCreditCardDisplayName,
    getIsCardExpired,
} from './SavedCreditCardCard.utils';

interface SavedCardRadioOptionProps extends React.HTMLAttributes<HTMLDivElement> {
    last4: string;
    brand: 'VISA' | 'AMEX' | 'MASTERCARD' | 'DISCOVER';
    expDate: string;
    nickname: string;
    entryId: string;
    isDefault?: boolean;
}

export function SavedCardRadioOption({
    last4,
    brand,
    expDate,
    nickname,
    isDefault,
    entryId,
    className,
    ...rest
}: SavedCardRadioOptionProps) {
    const isExpired = getIsCardExpired(expDate);
    const expMonth = expDate.substring(0, 2);
    const expYear = expDate.substring(2, 4);

    return (
        <div
            className={cx('digital-wallet-saved-card-radio-option', className)}
            {...rest}
        >
            <div className="image-text-group">
                <img
                    src={imageSrcMap[brand]}
                    alt={getCreditCardDisplayName(brand)}
                    className="cc-image"
                />

                <div>
                    <Text size="lg" fontWeight="semibold" className="title" tag="div">
                        <span className="mr-2">{nickname}</span>
                        {isDefault ? (
                            <Text
                                size="sm"
                                color="gray"
                                fontWeight="semibold"
                                className="default-payment"
                                tag="span"
                            >
                                (Default)
                            </Text>
                        ) : null}
                    </Text>
                    <div className="description">
                        {isDefault ? (
                            <>
                                <span className="corner" />
                                <Icon
                                    iconName="star"
                                    size="sm"
                                    className="default-star"
                                    aria-label="Default Payment Method"
                                />
                            </>
                        ) : null}

                        <div className="details-group">
                            <Text size="sm" color="gray" fontWeight="semibold">
                                {`Last 4 digits: ${last4}`}
                            </Text>
                            <Text
                                size="sm"
                                color="gray"
                                fontWeight="semibold"
                                className={isExpired ? 'expired' : undefined}
                            >
                                {isExpired ? <Icon iconName="warning" size="sm" /> : null}
                                <span>
                                    {isExpired ? 'Expired' : 'Expires'}: {expMonth}/
                                    {expYear}
                                </span>
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
